/**
 * Admin → create a user. Pre-creates a row (email/name/role/FA); it links to
 * Bayer SSO automatically on first login by email. Starts unapproved, so it
 * lands in the approval queue. Mirrors EditUserDialog's field layout.
 */

import { useState } from 'react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Field,
  Input,
  Option,
  Switch,
  tokens,
} from '@fluentui/react-components'
import { FOCUS_AREAS } from '../types/focus_area'
import type { AdminUserCreate, UserRoleStr } from '../types/admin'

const BASE_ROLES: { value: UserRoleStr; label: string }[] = [
  { value: 'member', label: 'Member' },
  { value: 'council', label: 'Council' },
]
const ADMIN_ROLES: { value: UserRoleStr; label: string }[] = [
  { value: 'admin', label: 'Admin' },
  { value: 'super_admin', label: 'Super Admin' },
]
const FA_ROLES = [
  { value: '', label: '—' },
  { value: 'lead', label: 'Lead' },
  { value: 'coach', label: 'Coach' },
]
const NO_FA = '__none__'

interface CreateUserDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreate: (payload: AdminUserCreate) => void
  /** super_admin sees admin-level role options. */
  canCreateAdminLevel?: boolean
  pending?: boolean
}

export function CreateUserDialog({
  open,
  onOpenChange,
  onCreate,
  canCreateAdminLevel,
  pending,
}: CreateUserDialogProps) {
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [role, setRole] = useState<UserRoleStr>('member')
  const [fa, setFa] = useState<string>(NO_FA)
  const [faRole, setFaRole] = useState<string>('')
  const [isTest, setIsTest] = useState(false)
  const [viewsAll, setViewsAll] = useState(false)

  const roles = canCreateAdminLevel ? [...BASE_ROLES, ...ADMIN_ROLES] : BASE_ROLES
  const valid = email.includes('@') && email.trim().length > 2 && name.trim().length > 0

  function reset() {
    setEmail('')
    setName('')
    setRole('member')
    setFa(NO_FA)
    setFaRole('')
    setIsTest(false)
    setViewsAll(false)
  }

  function handleOpenChange(next: boolean) {
    if (!next) reset()
    onOpenChange(next)
  }

  function submit() {
    if (!valid || pending) return
    onCreate({
      email: email.trim(),
      name: name.trim(),
      role,
      focus_area_code: fa === NO_FA ? undefined : fa,
      focus_area_role: faRole || undefined,
      is_test_account: isTest,
      views_all_demands: viewsAll,
    })
  }

  return (
    <Dialog open={open} onOpenChange={(_, d) => handleOpenChange(d.open)}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>New user</DialogTitle>
          <DialogContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM }}>
              <Field label="Email" required hint="Links to Bayer SSO on first sign-in.">
                <Input
                  type="email"
                  value={email}
                  onChange={(_, d) => setEmail(d.value)}
                  placeholder="firstname.lastname@bayer.com"
                />
              </Field>
              <Field label="Name" required>
                <Input value={name} onChange={(_, d) => setName(d.value)} placeholder="Full name" />
              </Field>
              <Field label="Role">
                <Dropdown
                  value={roles.find((r) => r.value === role)?.label || role}
                  selectedOptions={[role]}
                  onOptionSelect={(_, d) => setRole((d.optionValue as UserRoleStr) ?? role)}
                >
                  {roles.map((r) => (
                    <Option key={r.value} value={r.value}>
                      {r.label}
                    </Option>
                  ))}
                </Dropdown>
              </Field>
              <Field label="Focus area">
                <Dropdown
                  value={fa === NO_FA ? '—' : FOCUS_AREAS.find((f) => f.code === fa)?.name || fa}
                  selectedOptions={[fa]}
                  onOptionSelect={(_, d) => setFa((d.optionValue as string) ?? NO_FA)}
                >
                  <Option value={NO_FA}>—</Option>
                  {FOCUS_AREAS.map((f) => (
                    <Option key={f.code} value={f.code} text={`${f.code} · ${f.name}`}>
                      {f.code} · {f.name}
                    </Option>
                  ))}
                </Dropdown>
              </Field>
              <Field label="FA role">
                <Dropdown
                  value={FA_ROLES.find((r) => r.value === faRole)?.label || '—'}
                  selectedOptions={[faRole]}
                  onOptionSelect={(_, d) => setFaRole((d.optionValue as string) ?? '')}
                >
                  {FA_ROLES.map((r) => (
                    <Option key={r.value || 'none'} value={r.value}>
                      {r.label}
                    </Option>
                  ))}
                </Dropdown>
              </Field>
              <Switch
                checked={isTest}
                onChange={(_, d) => setIsTest(d.checked)}
                label="Test account"
              />
              {/* Pre-creating a row is how someone who has never signed in gets
                  access ready for them: Azure SSO links an existing row by
                  email on first login, so the grant is already there when they
                  arrive rather than something to come back and do (#18). */}
              <Switch
                checked={viewsAll}
                onChange={(_, d) => setViewsAll(d.checked)}
                label="Read all focus areas (no editing)"
              />
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button appearance="primary" disabled={!valid || pending} onClick={submit}>
              {pending ? 'Creating…' : 'Create user'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  )
}
